import { useTheme } from "../ThemeContext";
import { useState, useEffect, useCallback } from "react";

// Aceita "1,71" ou "1.71" — usuário brasileiro digita vírgula
const pvFloat = (s) => {
  if (typeof s === "number") return s;
  if (!s) return 0;
  const v = parseFloat(String(s).replace(",", "."));
  return isNaN(v) ? 0 : v;
};

const fmtNum = (n, casas = 2) => {
  if (!n && n !== 0) return "0,00";
  return n.toFixed(casas).replace(".", ",");
};

export default function Cambio({ lojaId }) {
  const { theme: C } = useTheme();
  const [taxas, setTaxas] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pdv_taxas")) || { pyg: "1.71", usd: "5.68" }; } catch { return { pyg: "1.71", usd: "5.68" }; }
  });
  const [salvo, setSalvo] = useState(false);
  const [carregando, setCarregando] = useState(true);

  // Carrega do servidor ao montar — sincroniza entre PCs/dispositivos
  const carregarDoServidor = useCallback(async () => {
    try {
      const r = await fetch(`https://mixupbr.com.br/api/loja_config/?loja_id=${lojaId || 1}&acao=get_cambio`);
      const d = await r.json();
      if (d && d.taxas && (d.taxas.pyg || d.taxas.usd)) {
        const tx = {
          pyg: String(d.taxas.pyg || "1.71").replace(",", "."),
          usd: String(d.taxas.usd || "5.68").replace(",", ".")
        };
        setTaxas(tx);
        localStorage.setItem("pdv_taxas", JSON.stringify(tx));
      }
    } catch (e) {
      // Falhou: mantém o que tá no localStorage
    }
    setCarregando(false);
  }, [lojaId]);

  useEffect(() => { carregarDoServidor(); }, [carregarDoServidor]);

  // Normaliza vírgula → ponto antes de salvar
  const salvar = () => {
    const pygN = pvFloat(taxas.pyg);
    const usdN = pvFloat(taxas.usd);
    if (pygN <= 0 || usdN <= 0) {
      alert("Digite valores válidos antes de salvar.");
      return;
    }
    const tx = { pyg: String(pygN), usd: String(usdN) };
    setTaxas(tx);
    localStorage.setItem("pdv_taxas", JSON.stringify(tx));
    fetch("https://mixupbr.com.br/api/loja_config/", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ loja_id: lojaId || 1, acao: "salvar_cambio", taxas: tx })
    }).catch(() => {});
    setSalvo(true); setTimeout(() => setSalvo(false), 2000);
  };

  // Aceita vírgula, ponto e dígitos — sem quebrar enquanto o usuário digita
  const onChangeTaxa = (campo, valor) => {
    const limpo = valor.replace(/[^\d.,]/g, "");
    setTaxas(t => ({ ...t, [campo]: limpo }));
  };

  // Indicadores de validade pra cada input
  const pygNum = pvFloat(taxas.pyg);
  const usdNum = pvFloat(taxas.usd);
  const pygValido = pygNum > 0;
  const usdValido = usdNum > 0;

  const sInput = (valido) => ({
    padding: "14px 16px",
    borderRadius: 10,
    border: `1px solid ${valido ? C.border : "#EF4444"}`,
    background: C.bg,
    color: valido ? C.text : "#EF4444",
    fontSize: 22,
    fontWeight: 800,
    textAlign: "center",
    outline: "none",
    fontFamily: "monospace",
    width: "100%",
    boxSizing: "border-box"
  });

  if (carregando) {
    return (
      <div style={{ maxWidth: 500, margin: "0 auto", padding: 40, textAlign: "center", color: C.textMuted }}>
        Carregando taxas do servidor...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 500, margin: "0 auto", animation: "fadeUp .4s ease-out" }}>
      <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>💱 Câmbio do Dia</h2>
      <p style={{ fontSize: 13, color: C.textMuted, marginBottom: 24 }}>Configure as taxas de câmbio que sua loja aceita hoje. Esses valores são usados no PDV.</p>

      {/* Guaraní */}
      <div style={{ padding: 20, borderRadius: 14, border: `1px solid ${C.border}`, background: C.card, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <span style={{ fontSize: 28 }}>🇵🇾</span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>Guaraní (PYG)</div>
            <div style={{ fontSize: 12, color: C.textMuted }}>Quantos Guaraníes valem R$ 1,00 (ex: 1450)</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 14, color: C.textMuted, fontWeight: 700 }}>R$ 1,00 =</span>
          <input
            value={taxas.pyg}
            onChange={e => onChangeTaxa("pyg", e.target.value)}
            inputMode="decimal"
            style={sInput(pygValido)}
            placeholder="1450"
          />
          <span style={{ fontSize: 14, color: C.textMuted, fontWeight: 700 }}>PYG</span>
        </div>
        {!pygValido && (
          <div style={{ marginTop: 6, fontSize: 11, color: "#EF4444", fontWeight: 600 }}>⚠ Valor inválido (use vírgula ou ponto)</div>
        )}
        <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: C.bg, textAlign: "center" }}>
          <span style={{ fontSize: 12, color: C.textMuted }}>Exemplo: </span>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#E8201A" }}>R$ 20,00</span>
          <span style={{ fontSize: 12, color: C.textMuted }}> = </span>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#22C55E" }}>₲ {fmtNum(20 * (pygNum || 1), 0).replace(",00", "")}</span>
        </div>
      </div>

      {/* Dólar */}
      <div style={{ padding: 20, borderRadius: 14, border: `1px solid ${C.border}`, background: C.card, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <span style={{ fontSize: 28 }}>🇺🇸</span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>Dólar (USD)</div>
            <div style={{ fontSize: 12, color: C.textMuted }}>Quantos Reais vale 1 Dólar</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 14, color: C.textMuted, fontWeight: 700 }}>1 USD =</span>
          <input
            value={taxas.usd}
            onChange={e => onChangeTaxa("usd", e.target.value)}
            inputMode="decimal"
            style={sInput(usdValido)}
            placeholder="5,68"
          />
          <span style={{ fontSize: 14, color: C.textMuted, fontWeight: 700 }}>BRL</span>
        </div>
        {!usdValido && (
          <div style={{ marginTop: 6, fontSize: 11, color: "#EF4444", fontWeight: 600 }}>⚠ Valor inválido (use vírgula ou ponto)</div>
        )}
        <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: C.bg, textAlign: "center" }}>
          <span style={{ fontSize: 12, color: C.textMuted }}>Exemplo: </span>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#3B82F6" }}>$ 100</span>
          <span style={{ fontSize: 12, color: C.textMuted }}> = </span>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#E8201A" }}>R$ {fmtNum(100 * (usdNum || 1))}</span>
        </div>
      </div>

      <button onClick={salvar} disabled={!pygValido || !usdValido}
        style={{ width: "100%", padding: 16, borderRadius: 12, border: "none", background: salvo ? "#22C55E" : (!pygValido || !usdValido ? "#6B7280" : "#E8201A"), color: "#fff", fontSize: 16, fontWeight: 800, cursor: (!pygValido || !usdValido) ? "not-allowed" : "pointer", transition: "all .2s", opacity: (!pygValido || !usdValido) ? 0.6 : 1 }}>
        {salvo ? "✓ Taxas salvas!" : "Salvar taxas do dia"}
      </button>

      <div style={{ marginTop: 16, padding: 14, borderRadius: 10, background: `${C.orange}10`, border: `1px solid ${C.orange}30` }}>
        <div style={{ fontSize: 12, color: C.orange, fontWeight: 700 }}>Dica: atualize as taxas todo dia antes de abrir o caixa. O PDV usa essas taxas pra converter automaticamente.</div>
      </div>
    </div>
  );
}
